/**
 * SupportReasonEditor — editor de texto enriquecido acotado para el campo
 * "detalle del caso" del widget de soporte remoto. Versión recortada de
 * RichTextEditor.tsx (control-interno, usado ahí para descripción de
 * tickets): solo negrita/cursiva/imagen — sin encabezados/listas/código/
 * video/@menciones, no hace falta tanto para describir un caso de soporte.
 *
 * El paste/drop de imágenes (Ctrl+V o arrastrar) sube el archivo a
 * control-interno (POST /external/support-attachments — sin autenticación,
 * mismo criterio sin sesión CRM que ya usa GET /external/business-hours) e
 * inserta el <img> resultante en el punto del cursor.
 */
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Image as ImageIcon, Italic, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

const CRM_API_BASE_URL = import.meta.env.VITE_CRM_API_BASE_URL as string | undefined;

/**
 * El bucket S3 es privado — la URL que devuelve /external/support-attachments
 * no carga directo en un <img> del navegador (control-interno la sirve
 * siempre a través de su proxy público /upload/img, ver resolveS3Url() en el
 * propio control-interno). Sin este wrap, la imagen recién pegada se veía
 * como ícono roto EN EL WIDGET aunque el detalle igual le llegaba bien al
 * agente (su vista sí pasa por ese proxy) — confuso para el usuario, que
 * creía que la imagen no se había adjuntado.
 */
function toProxyImageUrl(rawUrl: string): string {
  if (!CRM_API_BASE_URL) return rawUrl;
  return `${CRM_API_BASE_URL}/upload/img?url=${encodeURIComponent(rawUrl)}`;
}

function ToolBtn({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50 ${
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

interface SupportReasonEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function SupportReasonEditor({ value, onChange, placeholder }: SupportReasonEditorProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const handleImageFileRef = useRef<(file: File) => Promise<void>>(async () => {});

  const handleImageFile = async (file: File) => {
    if (!editor) return;
    if (!CRM_API_BASE_URL) {
      setUploadError('No se pudo subir la imagen (configuración incompleta)');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadError('La imagen excede el límite de 8 MB');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${CRM_API_BASE_URL}/external/support-attachments`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('upload failed');
      const data = (await res.json()) as { url: string };
      editor.chain().focus().setImage({ src: toProxyImageUrl(data.url) }).run();

      // setImage() deja el nodo de la imagen SELECCIONADO (NodeSelection),
      // no un cursor de texto colocado después. Si el usuario sigue
      // escribiendo justo después de pegar, ProseMirror interpreta que
      // quiere reemplazar lo seleccionado y BORRA la imagen para poner ahí
      // el texto tipeado — así se "comía" la última imagen pegada. Se
      // inserta SIEMPRE un párrafo vacío justo después de la imagen y se
      // mueve el cursor ahí, para que lo que se siga escribiendo entre en
      // un párrafo nuevo y nunca "encima" del nodo de la imagen. Va en un
      // `run()` aparte a propósito — si se encadenara con el setImage de
      // arriba en una sola llamada, un fallo acá podría abortar la
      // transacción COMPLETA (incluida la imagen ya subida a S3) sin
      // ningún error visible.
      const afterImagePos = editor.state.selection.to;
      editor.chain().insertContentAt(afterImagePos, { type: 'paragraph' }).focus('end').run();
    } catch {
      setUploadError('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };
  handleImageFileRef.current = handleImageFile;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, link: false, underline: false }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: placeholder ?? 'Contanos en qué necesitás ayuda…' }),
    ],
    editorProps: {
      handlePaste: (_view, event) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((item) => item.type.startsWith('image/'));
        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            void handleImageFileRef.current(file);
            return true;
          }
        }
        return false;
      },
      handleDrop: (_view, event, _slice, moved) => {
        const file = !moved ? event.dataTransfer?.files[0] : undefined;
        if (file?.type.startsWith('image/')) {
          event.preventDefault();
          void handleImageFileRef.current(file);
          return true;
        }
        return false;
      },
    },
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    autofocus: 'end',
  });

  if (!editor) return null;

  return (
    <div className="rounded-lg border border-border bg-background focus-within:border-primary">
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <ToolBtn
          title="Negrita (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={13} />
        </ToolBtn>
        <ToolBtn
          title="Cursiva (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={13} />
        </ToolBtn>
        <ToolBtn
          title={uploading ? 'Subiendo imagen...' : 'Adjuntar imagen (o pegar con Ctrl+V)'}
          disabled={uploading}
          onClick={() => imageInputRef.current?.click()}
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
        </ToolBtn>
      </div>

      <div className="support-reason-editor max-h-40 min-h-[72px] overflow-y-auto px-2.5 py-2 text-sm">
        <EditorContent editor={editor} />
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleImageFile(file);
          e.target.value = '';
        }}
      />

      {uploadError && <p className="px-2.5 pb-2 text-[11px] text-red-500">{uploadError}</p>}
    </div>
  );
}
